/** @format */

import { ApartmentOutlined, DeleteOutlined, StarFilled } from "@ant-design/icons";
import { Button, Descriptions, Drawer, Form, Popconfirm, Radio, Select, Space, Switch, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  apiOrganigrammaLinkSave,
  apiOrganigrammaRoleCreate,
  apiOrganigrammaStructureCreate,
  organigrammaErrorMessage,
} from "../../../../api/apiOrganigramma";
import {
  TOrgPerson,
  TOrgRole,
  TOrgStructure,
  TOrgUser,
} from "../../../../redux-modules/declarations/maps/organigramma";
import { showNotification } from "../../../../utils/showNotification";
import { subtreeIdsOf, TOrgIndexEntry } from "../utils/layout";
import { fullNameOf, nextStructureColors, surnameFirstOf } from "../utils/people";
import { EntitySelect } from "./EntitySelect";
import { OrgAvatar } from "./OrgAvatar";

export interface TPersonTarget {
  mode: "create" | "edit";
  /** create: persona preselezionata (null = da scegliere); edit: persona da modificare */
  userId: number | null;
  /** responsabile proposto (undefined = quello attuale / nessuno) */
  managerId?: number | null;
}

interface TFormValues {
  user_id?: number;
  manager_id?: number | null;
  role_id?: number | null;
  organigramma_structure_id?: number | null;
  link_type: "line" | "staff";
  is_coordinator: boolean;
}

interface Props {
  target: TPersonTarget | null;
  canUpdate: boolean;
  users: TOrgUser[];
  index: Map<number, TOrgIndexEntry>;
  roles: TOrgRole[];
  structures: TOrgStructure[];
  onRoleCreated: (role: TOrgRole) => void;
  onStructureCreated: (structure: TOrgStructure) => void;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onRemove: (personId: number) => void;
  onOpenPerson: (personId: number) => void;
}

const StructureDot = ({ structure }: { structure: TOrgStructure }): JSX.Element => (
  <span className="org-structure-option">
    <span
      className="org-structure-option__dot"
      style={{ background: structure.background_color, borderColor: structure.text_color }}
    />
    {structure.name}
  </span>
);

export const PersonDrawer = ({
  target,
  canUpdate,
  users,
  index,
  roles,
  structures,
  onRoleCreated,
  onStructureCreated,
  onClose,
  onSaved,
  onRemove,
  onOpenPerson,
}: Props): JSX.Element => {
  const [form] = Form.useForm<TFormValues>();
  const [saving, setSaving] = useState(false);

  const isEdit = target?.mode === "edit";
  const entry = isEdit && target?.userId ? index.get(target.userId) : undefined;
  const person: TOrgPerson | undefined = entry?.person;
  const readOnly = !canUpdate;

  const watchedUserId = Form.useWatch("user_id", form);
  const watchedManagerId = Form.useWatch("manager_id", form);
  const selectedUser = users.find(u => u.id === (isEdit ? target?.userId : watchedUserId));

  const directReports = person?.listaDipendenti || [];
  const hasReports = directReports.length > 0;

  // Popola il form ogni volta che cambia la persona / modalità
  useEffect(() => {
    if (!target || readOnly) return;
    form.resetFields();

    if (target.mode === "edit" && person) {
      form.setFieldsValue({
        manager_id: target.managerId !== undefined ? target.managerId : person.user_manager_id,
        role_id: person.role_id,
        organigramma_structure_id: person.organigramma_structure_id,
        link_type: person.is_staff ? "staff" : "line",
        is_coordinator: person.is_coordinator,
      });
    } else {
      const managerId = target.managerId ?? null;
      const manager = managerId !== null ? index.get(managerId)?.person : undefined;
      form.setFieldsValue({
        user_id: target.userId ?? undefined,
        manager_id: managerId,
        role_id: undefined,
        organigramma_structure_id: manager?.organigramma_structure_id ?? null,
        link_type: "line",
        is_coordinator: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // Senza responsabile non si può essere staff
  useEffect(() => {
    if (readOnly) return;
    if (!watchedManagerId && form.getFieldValue("link_type") === "staff") {
      form.setFieldsValue({ link_type: "line" });
    }
  }, [watchedManagerId, readOnly, form]);

  const userOptions = useMemo(
    () =>
      users
        .filter(u => !index.has(u.id) && !u.is_disabled)
        .map(u => ({ value: u.id, label: surnameFirstOf(u) }))
        .sort((a, b) => a.label.localeCompare(b.label, "it")),
    [users, index],
  );

  // Responsabili possibili: chi è nel grafico, esclusi la persona stessa, i suoi
  // collaboratori (eviterebbe un ciclo) e lo staff (che non ha collaboratori).
  const managerOptions = useMemo(() => {
    const excluded =
      isEdit && target?.userId ? subtreeIdsOf(index, target.userId) : new Set<number>();
    const options: { value: number; label: string; role: string }[] = [];
    index.forEach(({ person: p }) => {
      if (excluded.has(p.id) || p.is_staff) return;
      options.push({ value: p.id, label: surnameFirstOf(p), role: p.role_name });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [index, isEdit, target]);

  const createRole = async (name: string): Promise<TOrgRole> => {
    try {
      const role = await apiOrganigrammaRoleCreate(name);
      onRoleCreated({ ...role, users_count: role.users_count ?? 0 });
      return role;
    } catch (err) {
      showNotification("error", "Ruolo non creato", organigrammaErrorMessage(err, "Riprova."));
      throw err;
    }
  };

  const createStructure = async (name: string): Promise<TOrgStructure> => {
    try {
      const structure = await apiOrganigrammaStructureCreate({
        name,
        ...nextStructureColors(structures),
      });
      onStructureCreated({ ...structure, users_count: structure.users_count ?? 0 });
      return structure;
    } catch (err) {
      showNotification("error", "Struttura non creata", organigrammaErrorMessage(err, "Riprova."));
      throw err;
    }
  };

  const onValuesChange = (changed: Partial<TFormValues>): void => {
    // Nuovo responsabile e struttura vuota: propone quella del responsabile
    if ("manager_id" in changed && changed.manager_id && !form.getFieldValue("organigramma_structure_id")) {
      const manager = index.get(changed.manager_id)?.person;
      if (manager?.organigramma_structure_id) {
        form.setFieldsValue({ organigramma_structure_id: manager.organigramma_structure_id });
      }
    }
  };

  const submit = async (values: TFormValues): Promise<void> => {
    const userId = isEdit ? target?.userId : values.user_id;
    if (!userId || !values.role_id) return;

    setSaving(true);
    try {
      const response = await apiOrganigrammaLinkSave({
        user_id: userId,
        manager_id: values.manager_id ?? null,
        role_id: values.role_id,
        organigramma_structure_id: values.organigramma_structure_id ?? null,
        is_staff: values.link_type === "staff",
        is_coordinator: Boolean(values.is_coordinator),
      });
      showNotification("success", "Organigramma", response.message || "Modifiche salvate.");
      await onSaved();
      onClose();
    } catch (err) {
      showNotification("error", "Salvataggio non riuscito", organigrammaErrorMessage(err, "Riprova."));
    } finally {
      setSaving(false);
    }
  };

  const manager = person?.user_manager_id ? index.get(person.user_manager_id)?.person : undefined;
  const structure = structures.find(s => s.id === person?.organigramma_structure_id);

  const title = isEdit
    ? readOnly
      ? "Scheda"
      : "Modifica collocazione"
    : "Aggiungi all'organigramma";

  const removeConfirm = person ? (
    <div className="org-confirm">
      <strong>Togliere {fullNameOf(person)} dall'organigramma?</strong>
      {hasReports && (
        <div>
          {directReports.length === 1
            ? "Il suo collaboratore diretto passerà "
            : `I suoi ${directReports.length} collaboratori diretti passeranno `}
          {manager ? `a ${fullNameOf(manager)}.` : "in cima all'organigramma."}
        </div>
      )}
    </div>
  ) : null;

  const footer = readOnly ? null : (
    <div className="org-drawer-footer">
      <div>
        {isEdit && person && (
          <Popconfirm
            title={removeConfirm}
            okText="Togli"
            okButtonProps={{ danger: true }}
            cancelText="Annulla"
            onConfirm={(): void => onRemove(person.id)}
            placement="topLeft"
          >
            <Button danger type="text" icon={<DeleteOutlined />}>
              Togli dall'organigramma
            </Button>
          </Popconfirm>
        )}
      </div>
      <Space>
        <Button onClick={onClose}>Annulla</Button>
        <Button type="primary" loading={saving} onClick={(): void => form.submit()}>
          {isEdit ? "Salva modifiche" : "Aggiungi"}
        </Button>
      </Space>
    </div>
  );

  return (
    <Drawer
      title={title}
      placement="right"
      width={480}
      visible={Boolean(target)}
      onClose={onClose}
      footer={footer}
      className="org-drawer"
    >
      {(selectedUser || person) && (
        <div className="org-drawer-person">
          <OrgAvatar person={(person || selectedUser) as TOrgUser} size={52} />
          <div className="org-drawer-person__text">
            <div className="org-drawer-person__name">
              {fullNameOf(person || selectedUser)}
              {person?.is_coordinator && (
                <Tooltip title="Coordinatore">
                  <StarFilled className="org-node__coordinator" />
                </Tooltip>
              )}
            </div>
            <div className="org-drawer-person__role">
              {person ? person.role_name : "Non ancora nell'organigramma"}
            </div>
          </div>
        </div>
      )}

      {readOnly && person ? (
        <Descriptions column={1} size="small" bordered className="org-drawer-details">
          <Descriptions.Item label="Ruolo">{person.role_name}</Descriptions.Item>
          <Descriptions.Item label="Responsabile">
            {manager ? (
              <Button type="link" size="small" onClick={(): void => onOpenPerson(manager.id)}>
                {fullNameOf(manager)}
              </Button>
            ) : (
              "Nessuno (vertice)"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Struttura">
            {structure ? <StructureDot structure={structure} /> : "Nessuna"}
          </Descriptions.Item>
          <Descriptions.Item label="Collegamento">
            {person.is_staff ? "Staff" : "Linea gerarchica"}
          </Descriptions.Item>
          <Descriptions.Item label="Coordinatore">{person.is_coordinator ? "Sì" : "No"}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Form<TFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={submit}
          onValuesChange={onValuesChange}
          initialValues={{ link_type: "line", is_coordinator: false }}
        >
          {!isEdit && (
            <Form.Item
              name="user_id"
              label="Persona"
              rules={[{ required: true, message: "Scegli chi collocare" }]}
            >
              <Select
                showSearch
                placeholder="Cerca per nome o cognome"
                options={userOptions}
                optionFilterProp="label"
                notFoundContent="Tutte le persone attive sono già nell'organigramma"
              />
            </Form.Item>
          )}

          <Form.Item
            name="manager_id"
            label="Responsabile"
            extra="Lascia vuoto per metterla in cima all'organigramma."
          >
            <Select
              showSearch
              allowClear
              placeholder="Nessuno: vertice dell'organigramma"
              optionFilterProp="label"
              optionLabelProp="label"
            >
              {managerOptions.map(o => (
                <Select.Option key={o.value} value={o.value} label={o.label}>
                  <div className="org-option">
                    <span>{o.label}</span>
                    <span className="org-option__meta">{o.role}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="role_id"
            label="Ruolo"
            rules={[{ required: true, message: "Scegli o crea un ruolo" }]}
            extra="Non c'è? Scrivilo e crealo al volo."
          >
            <EntitySelect<TOrgRole>
              items={roles}
              entityLabel="il ruolo"
              placeholder="Cerca o crea un ruolo"
              onCreate={createRole}
            />
          </Form.Item>

          <Form.Item
            name="organigramma_structure_id"
            label="Struttura"
            extra="Le persone della stessa struttura sono racchiuse in un riquadro colorato."
          >
            <EntitySelect<TOrgStructure>
              items={structures}
              entityLabel="la struttura"
              placeholder="Nessuna struttura"
              allowClear
              onCreate={createStructure}
              renderItem={(s): JSX.Element => <StructureDot structure={s} />}
            />
          </Form.Item>

          <Form.Item
            name="link_type"
            label="Tipo di collegamento"
            extra={
              !watchedManagerId
                ? "Lo staff richiede un responsabile."
                : hasReports
                ? "Ha collaboratori diretti: può essere solo in linea gerarchica."
                : "Lo staff è disegnato a lato del responsabile, con linea tratteggiata."
            }
          >
            <Radio.Group buttonStyle="solid" className="org-segmented-radio">
              <Radio.Button value="line">
                <ApartmentOutlined /> Linea gerarchica
              </Radio.Button>
              <Radio.Button value="staff" disabled={!watchedManagerId || hasReports}>
                Staff
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="is_coordinator" label="Coordinatore" valuePropName="checked">
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      )}

      {person && hasReports && (
        <div className="org-drawer-reports">
          <div className="org-drawer-reports__title">Collaboratori diretti ({directReports.length})</div>
          <ul>
            {directReports.map(r => (
              <li key={r.id}>
                <button type="button" onClick={(): void => onOpenPerson(r.id)}>
                  <OrgAvatar person={r} size={26} />
                  <span className="org-drawer-reports__name">{fullNameOf(r)}</span>
                  <span className="org-drawer-reports__role">{r.is_staff ? "Staff" : r.role_name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
};
